import type { SeriesMetric, SeriesRange } from '@st/contract';
import type { TFunction } from '../i18n/I18nProvider';

/** Rasponi koje portal nudi — isti skup kao `RANGE_DAYS` u `apps/api`. */
export const SERIES_RANGES: readonly SeriesRange[] = ['1w', '1m', '3m', '6m', '1y'] as const;

/**
 * Metrike koje portal prikazuje. `steps`/`active_calories`/`sleep_minutes`
 * dolaze iz mobilnog Health importa pa ih portal namjerno ne nudi.
 */
export const SERIES_METRICS: readonly SeriesMetric[] = [
  'weight', 'waist', 'chest', 'hips', 'arm', 'thigh', 'body_fat',
  'volume', 'workouts', 'duration',
] as const;

const METRIC_KEY: Record<string, string> = {
  weight: 'chart.metricWeight',
  waist: 'chart.metricWaist',
  chest: 'chart.metricChest',
  hips: 'chart.metricHips',
  arm: 'chart.metricArm',
  thigh: 'chart.metricThigh',
  body_fat: 'chart.metricBodyFat',
  volume: 'chart.metricVolume',
  workouts: 'chart.metricWorkouts',
  duration: 'chart.metricDuration',
};

const RANGE_KEY: Record<SeriesRange, string> = {
  '1w': 'chart.range1w',
  '1m': 'chart.range1m',
  '3m': 'chart.range3m',
  '6m': 'chart.range6m',
  '1y': 'chart.range1y',
};

export function metricLabel(metric: SeriesMetric, t: TFunction): string {
  return t(METRIC_KEY[metric] ?? metric);
}

export function rangeLabel(range: SeriesRange, t: TFunction): string {
  return t(RANGE_KEY[range]);
}

export function isSeriesMetric(value: string): value is SeriesMetric {
  return (SERIES_METRICS as readonly string[]).includes(value);
}

export function isSeriesRange(value: string): value is SeriesRange {
  return (SERIES_RANGES as readonly string[]).includes(value);
}

/**
 * Formatiranje vrijednosti za os i sažetak. Broj decimala ovisi o metrici:
 * broj treninga je cijeli broj, težina ima jednu decimalu.
 */
export function formatMetric(value: number, metric: SeriesMetric): string {
  if (metric === 'workouts' || metric === 'duration' || metric === 'volume') {
    return String(Math.round(value));
  }
  return String(Math.round(value * 10) / 10);
}

/** Predznak se piše eksplicitno — „−1,2 kg” i „+1,2 kg” nose različitu vijest. */
export function formatChange(value: number, metric: SeriesMetric): string {
  const formatted = formatMetric(Math.abs(value), metric);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}
